import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";

const GRAVITY        = 0.18;
const RESTITUTION    = 0.82;  // energy kept on bounce
const MIN_BOUNCE     = 3.5;   // minimum upward speed after floor bounce
const SQUASH_DECAY   = 0.70;  // how fast squash fades (lower = snappier)

function GlowBalls({ configs, className }) {
  const containerRef = useRef(null);
  const ballRefs     = useRef([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const w = () => container.offsetWidth;
    const h = () => container.offsetHeight;

    const balls = configs.map((cfg, i) => ({
      el:      ballRefs.current[i],
      r:       cfg.r,
      x:       cfg.r + Math.random() * (w() - cfg.r * 2),
      y:       cfg.r + Math.random() * (h() * 0.55),        // start in upper half
      vx:      (Math.random() > 0.5 ? 1 : -1) * (1.8 + Math.random() * 2.5),
      vy:      -(2.5 + Math.random() * 3.5),                // start moving upward
      squashV: 0,
      squashH: 0,
    }));

    balls.forEach((b) =>
      gsap.set(b.el, { x: b.x - b.r, y: b.y - b.r, transformOrigin: "50% 50%" })
    );

    const tick = () => {
      const cw = w();
      const ch = h();

      balls.forEach((b) => {
        // Gravity
        b.vy += GRAVITY;

        // Move
        b.x += b.vx;
        b.y += b.vy;

        // Wall collisions
        let bouncedH = false;
        let bouncedV = false;

        if (b.x - b.r < 0) {
          b.x  = b.r;
          b.vx = Math.abs(b.vx) * RESTITUTION;
          bouncedH = true;
        }
        if (b.x + b.r > cw) {
          b.x  = cw - b.r;
          b.vx = -Math.abs(b.vx) * RESTITUTION;
          bouncedH = true;
        }
        if (b.y - b.r < 0) {
          b.y  = b.r;
          b.vy = Math.abs(b.vy) * RESTITUTION;
          bouncedV = true;
        }
        if (b.y + b.r > ch) {
          b.y  = ch - b.r;
          b.vy = -Math.abs(b.vy) * RESTITUTION;
          // Keep balls alive — always bounce with at least MIN_BOUNCE
          if (Math.abs(b.vy) < MIN_BOUNCE) b.vy = -MIN_BOUNCE;
          bouncedV = true;
        }

        // Set squash timers on collision
        if (bouncedV) b.squashV = 1;
        if (bouncedH) b.squashH = 1;

        // Decay squash
        b.squashV *= SQUASH_DECAY;
        b.squashH *= SQUASH_DECAY;

        // --- Squash & stretch ---
        // Stretch along velocity vector (no CSS rotation needed):
        // elongate along travel direction, squish perpendicular to it.
        const speed = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
        let sx = 1;
        let sy = 1;

        if (speed > 0.5) {
          const nx      = b.vx / speed;
          const ny      = b.vy / speed;
          const stretch = Math.min(speed * 0.024, 0.32);
          const elong   = 1 + stretch;
          const squish  = 1 / elong;
          // Project elongation onto screen X and Y axes
          sx = elong * nx * nx + squish * ny * ny;
          sy = elong * ny * ny + squish * nx * nx;
        }

        // Add squash on bounce (floor squash = vertical flatten; wall squash = horizontal flatten)
        if (b.squashV > 0.04) {
          sy *= (1 - b.squashV * 0.50);
          sx *= (1 + b.squashV * 0.40);
        }
        if (b.squashH > 0.04) {
          sx *= (1 - b.squashH * 0.50);
          sy *= (1 + b.squashH * 0.40);
        }

        // Clamp to avoid extreme deformation
        sx = Math.max(0.50, Math.min(1.65, sx));
        sy = Math.max(0.50, Math.min(1.65, sy));

        gsap.set(b.el, {
          x:       b.x - b.r,
          y:       b.y - b.r,
          scaleX:  sx,
          scaleY:  sy,
        });
      });
    };

    gsap.ticker.add(tick);
    return () => gsap.ticker.remove(tick);
  }, []); // eslint-disable-line

  return (
    <div ref={containerRef} className={`mi-glowballs ${className || ""}`}>
      {configs.map((cfg, i) => (
        <div
          key={i}
          ref={(el) => (ballRefs.current[i] = el)}
          className="mi-glowball"
          style={{
            width:      `${cfg.r * 2}px`,
            height:     `${cfg.r * 2}px`,
            background: `radial-gradient(circle at 35% 35%, #fff 0%, ${cfg.color} 35%, rgba(0,0,0,0) 100%)`,
            boxShadow:  `0 0 40px 15px ${cfg.glow}`,
          }}
        />
      ))}
    </div>
  );
}

export default GlowBalls;
